import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Copy, ChevronDown, ChevronRight, Lightbulb, ListChecks, ChevronsUpDown } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { PdfButton } from '../report/ReportBlocks';

const COLORS = [
  { bg: 'bg-blue-500/10', text: 'text-blue-600', border: 'border-blue-500/20', accent: 'bg-blue-500' },
  { bg: 'bg-purple-500/10', text: 'text-purple-600', border: 'border-purple-500/20', accent: 'bg-purple-500' },
  { bg: 'bg-teal-500/10', text: 'text-teal-600', border: 'border-teal-500/20', accent: 'bg-teal-500' },
  { bg: 'bg-amber-500/10', text: 'text-amber-600', border: 'border-amber-500/20', accent: 'bg-amber-500' },
  { bg: 'bg-red-500/10', text: 'text-red-600', border: 'border-red-500/20', accent: 'bg-red-500' },
  { bg: 'bg-emerald-500/10', text: 'text-emerald-600', border: 'border-emerald-500/20', accent: 'bg-emerald-500' },
];

const copy = (text, msg) => { navigator.clipboard.writeText(text); toast.success(msg); };

const asText = (data) => [
  ...(data.intake_checklist?.length ? ['MUST-CONFIRM CHECKLIST', ...data.intake_checklist.map((c) => `[ ] ${c}`), ''] : []),
  ...(data.sections || []).flatMap((sec, si) => [
    `SECTION ${si + 1}: ${sec.section_name}`, sec.section_description || '',
    ...(sec.questions || []).flatMap((q, qi) => [
      `Q${qi + 1}. ${q.question}`, ...(q.why_ask ? [`   Why ask: ${q.why_ask}`] : []),
      ...(q.follow_up_hints || []).map((h) => `     - ${h}`), '',
    ]),
  ]),
].join('\n').trim();

const Question = ({ q, qi, si, colors }) => (
  <div className="p-3 rounded-lg border bg-muted/10" data-testid={`discovery-question-${si}-${qi}`}>
    <div className="flex items-start justify-between gap-2">
      <div className="flex items-start gap-2 min-w-0">
        <Badge className={`${colors.accent} text-white text-[10px] shrink-0 mt-0.5`}>{qi + 1}</Badge>
        <p className="text-sm font-medium leading-relaxed">{q.question}</p>
      </div>
      <Button variant="ghost" size="sm" className="h-6 text-xs gap-1 shrink-0" onClick={() => copy(q.question, 'Question copied')} data-testid="copy-question-btn">
        <Copy className="h-3 w-3" /> Copy
      </Button>
    </div>
    {q.why_ask && (
      <div className="flex items-start gap-1.5 mt-2 ml-7">
        <Lightbulb className="h-3.5 w-3.5 text-amber-500 shrink-0 mt-0.5" />
        <p className="text-xs text-muted-foreground italic">Why ask: {q.why_ask}</p>
      </div>
    )}
    {q.follow_up_hints?.length > 0 && (
      <div className="mt-2 ml-7 pl-3 border-l-2 border-muted">
        <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider mb-1">Follow-up angles</p>
        <ul className="space-y-0.5">{q.follow_up_hints.map((h, hi) => <li key={hi} className="text-xs text-muted-foreground">– {h}</li>)}</ul>
      </div>
    )}
  </div>
);

const SectionAccordion = ({ section, si, open }) => {
  const [expanded, setExpanded] = useState(open);
  useEffect(() => { setExpanded(open); }, [open]);
  const colors = COLORS[si % COLORS.length];
  const questions = section.questions || [];
  return (
    <Card className={`border ${colors.border} overflow-hidden`} data-testid={`discovery-section-${si}`}>
      <button className="w-full flex items-center justify-between p-4 text-left hover:bg-muted/30 transition-colors" onClick={() => setExpanded(!expanded)} data-testid={`discovery-section-toggle-${si}`}>
        <div className="flex items-center gap-3 min-w-0">
          <div className={`w-8 h-8 rounded-lg ${colors.bg} flex items-center justify-center shrink-0`}><span className={`text-sm font-bold ${colors.text}`}>{si + 1}</span></div>
          <div className="min-w-0">
            <h3 className="text-sm font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>{section.section_name}</h3>
            <p className="text-xs text-muted-foreground">{section.section_description}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Badge variant="secondary" className="text-xs">{questions.length} questions</Badge>
          {expanded ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
        </div>
      </button>
      {expanded && <CardContent className="pt-0 pb-4 px-4 space-y-3">{questions.map((q, qi) => <Question key={qi} q={q} qi={qi} si={si} colors={colors} />)}</CardContent>}
    </Card>
  );
};

const Checklist = ({ items }) => (
  <Card data-testid="discovery-checklist">
    <CardHeader className="pb-2">
      <CardTitle className="text-sm flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
        <ListChecks className="h-4 w-4 text-teal-500" /> Must-confirm checklist before you hang up
      </CardTitle>
    </CardHeader>
    <CardContent className="grid md:grid-cols-2 gap-x-6 gap-y-1.5">
      {items.map((it, i) => (
        <label key={i} className="flex items-start gap-2 text-sm cursor-pointer">
          <input type="checkbox" className="mt-1 accent-teal-600" data-testid={`discovery-checklist-item-${i}`} /> <span>{it}</span>
        </label>
      ))}
    </CardContent>
  </Card>
);

// One Discovery Call guide view for the job page, shared links and the combined share
export const DiscoveryReportView = ({ data, pdfUrl, actions }) => {
  const [allOpen, setAllOpen] = useState(false);
  const sections = data?.sections || [];
  return (
    <div className="space-y-4" data-testid="discovery-report-display">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h3 className="text-lg font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>Discovery Call Questions</h3>
          <div className="flex items-center gap-2 mt-1">
            <Badge variant="secondary" className="text-xs" data-testid="discovery-section-count">{sections.length} Sections</Badge>
            <Badge variant="secondary" className="text-xs" data-testid="discovery-question-count">{data?.total_questions || 0} Questions</Badge>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          {actions}
          <Button variant="outline" size="sm" className="gap-1.5" onClick={() => setAllOpen(!allOpen)} data-testid="discovery-expand-all-btn">
            <ChevronsUpDown className="h-3.5 w-3.5" /> {allOpen ? 'Collapse all' : 'Expand all'}
          </Button>
          <Button variant="outline" size="sm" className="gap-1.5" onClick={() => copy(asText(data || {}), 'All questions copied')} data-testid="discovery-copy-all-btn">
            <Copy className="h-3.5 w-3.5" /> Copy All
          </Button>
          {pdfUrl && <PdfButton url={pdfUrl} testid="discovery-download-pdf-btn" />}
        </div>
      </div>
      {data?.intake_checklist?.length > 0 && <Checklist items={data.intake_checklist} />}
      <div className="space-y-3">{sections.map((s, si) => <SectionAccordion key={si} section={s} si={si} open={allOpen} />)}</div>
    </div>
  );
};

export default DiscoveryReportView;
